import { z } from 'zod';

// Matches the real API's phone example (+2348012345678) — international
// format, leading +, 8-15 digits total. Exported since a couple of other
// real-backend forms (e.g. admin's Onboard Client) validate phone the same
// way the docs describe for registration.
export const phoneRegex = /^\+[1-9]\d{7,14}$/;

// The API's own example password ("stringst") is 8 characters with no
// complexity requirement documented — validate length only here and let
// the server be the source of truth on anything stricter.
const passwordField = z.string().min(8, 'At least 8 characters');

export const registerSchema = z
  .object({
    first_name: z.string().min(1, 'Enter your first name'),
    last_name: z.string().min(1, 'Enter your last name'),
    email: z.string().email('Enter a valid email'),
    phone: z.string().regex(phoneRegex, 'Use international format, e.g. +2348012345678'),
    password: passwordField,
    password_confirmation: z.string(),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

export const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

export const magicLinkSchema = z.object({
  email: z.string().email('Enter a valid email'),
});

export const forgotPasswordSchema = z.object({
  email: z.string().email('Enter a valid email'),
});

// /auth/reset-password takes email + otp + password in a single call — no
// separate "verify the code" step on this API.
export const resetPasswordSchema = z
  .object({
    email: z.string().email('Enter a valid email'),
    otp: z.string().length(6, 'Enter the 6-digit code').regex(/^\d{6}$/, 'Code must be numeric'),
    password: passwordField,
    password_confirmation: z.string(),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1, 'Enter your current password'),
    new_password: passwordField,
    new_password_confirmation: z.string(),
  })
  .refine((data) => data.new_password === data.new_password_confirmation, {
    message: 'Passwords do not match',
    path: ['new_password_confirmation'],
  })
  .refine((data) => data.new_password !== data.current_password, {
    message: 'New password must be different from your current password',
    path: ['new_password'],
  });

// Shared by email verification (registration) and admin 2FA — both are a
// 6-digit numeric OTP, field name "otp" on the real API.
export const otpSchema = z.object({
  otp: z.string().length(6, 'Enter the 6-digit code').regex(/^\d{6}$/, 'Code must be numeric'),
});
